import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    createSearchParams,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';

import { useTelemetryContext } from '@myphx-engineering/instrumentation/react';
import {
    geolocationDistance,
    useGeolocation,
} from '@myphx-engineering/location-utils';
import {
    Alert,
    FilterSelection,
    FullBleed,
    IconLocalShipping,
    IconLocationOn,
    IconShoppingBag,
    IconWarning,
    Stack,
    Tabs,
} from '@myphx-engineering/phx-design-system';
import {
    useDisclosure,
    useIsMobile,
} from '@myphx-engineering/phx-design-system/hooks';

import {
    ControlledSubstanceCode,
    type PatientSavingsRecommendationInfoFragment,
    type PlanSavingsRecommendationInfoFragment,
    type PrescriberInfoFragment,
    type ProviderOffersInfoFragment,
    SuggestedProviderOffersReason,
} from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';
import { useNumericSearchParam } from '../../hooks/use-numeric-search-param';
import { getAbsoluteRoute, getPrimaryProvider } from '../../util';
import { sortOffersByPrice } from '../../util/offers/sort';
import { InfoHeading } from '../common/InfoHeading';
import { LocationPickerCover } from '../location-picker-cover';
import { ConditionalComponent } from '../utility/ConditionalComponent';

import { AlternativesLink } from './alternatives-link/AlternativesLink';
import { RecommendationsLink } from './alternatives-link/RecommendationsLink';
import { ProviderOffersCard, ProviderOffersCardList } from './cards';
import { NoLocation } from './cards/NoLocation';
import { InstantRoutingModal } from './modals';

type ProviderSelectionProps = {
    header: React.ReactNode;
    recommendations?: (
        | PatientSavingsRecommendationInfoFragment
        | PlanSavingsRecommendationInfoFragment
    )[];
    providerOffersByType: {
        delivery: ProviderOffersInfoFragment[];
        pickup: ProviderOffersInfoFragment[];
    };
    onSelect: (providerOffersId: string, providerId?: string) => void;
    featuredProvider?: {
        providerOffers: ProviderOffersInfoFragment;
        reason: SuggestedProviderOffersReason;
    };
    prescriber?: PrescriberInfoFragment;
    hasInstantRouting?: boolean;
    controlledSubstanceCode?: ControlledSubstanceCode | null;
    simplified?: boolean;
};

export const ProviderSelection = ({
    header,
    recommendations,
    providerOffersByType,
    onSelect,
    featuredProvider,
    prescriber,
    hasInstantRouting,
    controlledSubstanceCode,
    simplified,
}: ProviderSelectionProps) => {
    const { telemetryInstance } = useTelemetryContext();

    const { t } = useTranslation();
    const { geolocation } = useGeolocation();
    const [opened, { open, close }] = useDisclosure(false);
    const navigate = useNavigate();
    const isMobile = useIsMobile('md');
    const [searchParams, setSearchParms] = useSearchParams();

    const hasPricing = [
        ...providerOffersByType.delivery,
        ...providerOffersByType.pickup,
    ].some((providerOffer) => {
        return providerOffer.offers.some(
            (offer) =>
                offer.__typename === 'CashOffer' ||
                offer.__typename === 'InsuranceOffer'
        );
    });

    const sortedPickupOffers = useMemo(() => {
        return sortOffersByPrice(
            providerOffersByType.pickup.map((offer) => {
                const primaryPharmacy = getPrimaryProvider(offer, geolocation);
                const distance =
                    primaryPharmacy &&
                    geolocation &&
                    primaryPharmacy.__typename === 'PharmacyProvider'
                        ? geolocationDistance(geolocation, {
                              latitude: primaryPharmacy.latitude,
                              longitude: primaryPharmacy.longitude,
                          })
                        : Infinity;
                return { ...offer, distance };
            })
        );
    }, [providerOffersByType.pickup, geolocation]);
    const sortedDeliveryOffers = useMemo(() => {
        return sortOffersByPrice(providerOffersByType.delivery);
    }, [providerOffersByType.delivery]);

    const [
        pickupFiltersOpened,
        { open: openPickupFilters, close: closePickupFilters },
    ] = useDisclosure(false);
    const [
        deliveryFiltersOpened,
        { open: openDeliveryFilters, close: closeDeliveryFilters },
    ] = useDisclosure(false);

    const distance = useNumericSearchParam('within');
    const { prescriptionId } = useParams();

    const handleAltsClick = () => {
        if (prescriptionId && featuredProvider) {
            navigate({
                pathname: getAbsoluteRoute('cabinet.rxId.alternatives', {
                    prescriptionId,
                }),
                search: createSearchParams({
                    within: distance.toString(),
                }).toString(),
            });
        }
    };

    const defaultTab = searchParams.get('tab') || 'pickup';

    const displayInstantRoutingModal =
        controlledSubstanceCode && prescriber && hasInstantRouting;

    const { recommendationAlternativesEnabled } = useFlags();

    return (
        <Stack gap="lg">
            {displayInstantRoutingModal ? <InstantRoutingModal /> : null}
            {simplified ? null : (
                <Stack gap="sm">
                    {header}
                    {recommendationAlternativesEnabled ? (
                        <RecommendationsLink
                            onAltsClick={handleAltsClick}
                            recommendations={recommendations}
                        />
                    ) : (
                        <AlternativesLink
                            onAltsClick={handleAltsClick}
                            cheapestOffers={featuredProvider?.providerOffers}
                        />
                    )}
                </Stack>
            )}

            <FullBleed color={simplified ? `body` : `gradient`}>
                <Stack gap="lg" role="list">
                    <InfoHeading
                        title={t('pickYourPharmacy.title')}
                        informationDialog={{
                            title: t('pickYourPharmacy.infoDialog.title'),
                            content: t('pickYourPharmacy.infoDialog.body'),
                        }}
                    />
                    <LocationPickerCover isOpen={opened} onClose={close} />
                    <ConditionalComponent
                        condition={!hasPricing && !!geolocation}
                    >
                        <Alert severity="info" IconOverride={IconWarning}>
                            {t(
                                simplified
                                    ? 'pickYourPharmacy.noPricesSimplified'
                                    : 'pickYourPharmacy.noPrices'
                            )}
                        </Alert>
                    </ConditionalComponent>
                    <FilterSelection
                        inputTestId="pick-your-pharmacy-location-picker-button"
                        input={
                            geolocation?.name ??
                            t(
                                'pickYourPharmacy.location.setLocationAndDistance'
                            )
                        }
                        StartIcon={IconLocationOn}
                        onInputClick={open}
                        distance={
                            geolocation?.name
                                ? `${distance} ${t('common.distance.distanceUnit')}`
                                : undefined
                        }
                    />
                    {featuredProvider && (
                        <ProviderOffersCard
                            role="listitem"
                            suggestedReasons={[featuredProvider.reason]}
                            providerOffers={featuredProvider.providerOffers}
                            onSelected={onSelect}
                        />
                    )}
                </Stack>
            </FullBleed>

            {geolocation ? (
                <Tabs
                    defaultValue={defaultTab}
                    grow
                    onChange={(value: string | null) => {
                        if (value) {
                            setSearchParms((searchParams) => {
                                searchParams.set('tab', value);
                                return searchParams;
                            });
                            telemetryInstance.logEvent({
                                name: `Provider select view toggle, ${value}`,
                                source: 'ProviderSelection',
                            });
                        }
                    }}
                    tabBarProps={{ fullBleed: isMobile }}
                    tabs={[
                        {
                            label: t('pickYourPharmacy.pickUp'),
                            panel: (
                                <FullBleed breakpoint="md" color="gray">
                                    <ProviderOffersCardList
                                        providerOffers={sortedPickupOffers}
                                        onSelect={onSelect}
                                        pickUp
                                        filterOpen={pickupFiltersOpened}
                                        openFilter={openPickupFilters}
                                        closeFilter={closePickupFilters}
                                        showContactForPrice={
                                            hasPricing && !simplified
                                        }
                                        simplified={simplified}
                                    />
                                </FullBleed>
                            ),
                            value: 'pickup',
                            LeftIcon: IconShoppingBag,
                        },
                        {
                            label: t('pickYourPharmacy.delivery'),
                            panel: (
                                <FullBleed breakpoint="md" color="gray">
                                    <ProviderOffersCardList
                                        providerOffers={sortedDeliveryOffers}
                                        onSelect={onSelect}
                                        filterOpen={deliveryFiltersOpened}
                                        openFilter={openDeliveryFilters}
                                        closeFilter={closeDeliveryFilters}
                                        showContactForPrice={
                                            hasPricing && !simplified
                                        }
                                        simplified={simplified}
                                    />
                                </FullBleed>
                            ),
                            value: 'delivery',
                            LeftIcon: IconLocalShipping,
                        },
                    ]}
                />
            ) : (
                <NoLocation />
            )}
        </Stack>
    );
};
